import React from 'react';
import PhoneInputBase, { getCountryCallingCode } from 'react-phone-number-input';
import type { Country } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { cn } from '@/lib/utils';

export interface PhoneInputProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  onBlur?: () => void;
  defaultCountry?: Country;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
  hasError?: boolean;
}

const PhoneInput = React.forwardRef<HTMLDivElement, PhoneInputProps>(
  (
    {
      value,
      onChange,
      onBlur,
      defaultCountry = 'US',
      placeholder = '+1 (234) 567-890',
      className,
      id,
      disabled,
      hasError,
    },
    ref
  ) => {
    // Keep track of the currently selected country code to retain the dial prefix
    const [selectedCountry, setSelectedCountry] = React.useState<Country>(defaultCountry);

    // Get the corresponding calling code (e.g., '1' for US)
    const callingCode = React.useMemo(() => {
      try {
        return `+${getCountryCallingCode(selectedCountry)}`;
      } catch {
        return '+1'; // Fallback
      }
    }, [selectedCountry]);

    return (
      <div ref={ref} className={cn('phone-input-wrapper', className)}>
        <PhoneInputBase
          international
          countryCallingCodeEditable={false}
          defaultCountry={defaultCountry}
          // On selection change, sync our active country
          onCountryChange={(country) => {
            if (country) setSelectedCountry(country);
          }}
          value={value || ''}
          onBlur={onBlur}
          onChange={(val) => {
            // 1. If the user clears the input completely or it falls shorter than the expected calling code
            if (!val || !val.startsWith(callingCode)) {
              // Reset/Enforce the calling code prefix instead of letting it go empty
              onChange(callingCode);
              return;
            }

            // 2. Restrict to max 10 digits (excluding country code)
            const digitsOnly = val.replace(/\D/g, '');
            const countryCodeDigits = callingCode.replace(/\D/g, '').length;
            const nationalDigits = digitsOnly.length - countryCodeDigits;

            if (nationalDigits > 10) return;

            onChange(val);
          }}
          placeholder={placeholder}
          disabled={disabled}
          id={id}
          limitMaxLength
          className={cn(
            'flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring md:text-sm',
            hasError ? 'border-destructive' : 'border-input',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        />
        <style>{`
          .phone-input-wrapper .PhoneInput {
            gap: 0.5rem;
            background: transparent;
          }
          .phone-input-wrapper .PhoneInputCountry {
            margin-right: 0;
            background: transparent;
          }
          .phone-input-wrapper .PhoneInputCountrySelect {
            background: transparent !important;
            color: inherit;
            border: none;
          }
          .phone-input-wrapper .PhoneInputInput {
            flex: 1;
            background: transparent !important;
            border: none;
            outline: none;
            font-size: inherit;
            color: inherit;
          }
          .phone-input-wrapper .PhoneInputInput::placeholder {
            color: hsl(var(--muted-foreground));
          }
          .phone-input-wrapper .PhoneInputCountryIcon {
            width: 1.25rem;
            height: 0.9rem;
            background: transparent;
          }
          .phone-input-wrapper .PhoneInputCountrySelectArrow {
            margin-left: 0.25rem;
            opacity: 0.5;
            color: inherit;
          }
          .phone-input-wrapper select {
            background-color: transparent !important;
            color: inherit !important;
          }
          .phone-input-wrapper select option {
            background-color: #000;
            color: #fff;
          }
        `}</style>
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };